import React, { useEffect, useState } from 'react';
import useEditFormScroll, { editHighlightStyle } from '../hooks/useEditFormScroll';
import useIsMobile from '../hooks/useIsMobile';

function ItemMaster({ token, selectedPropertyId }) {
  const { isMobile } = useIsMobile();
  const emptyForm = { main_item_code: '', main_item_name: '', liquor_category_id: '', brand: '', hsn_code: '', quantity_ml_per_unit: '' };
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState('');
  const [editingMainCode, setEditingMainCode] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [uploadResult, setUploadResult] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [autoGenerateCodes, setAutoGenerateCodes] = useState(false);
  const [uploading, setUploading] = useState(false);
  const { formRef, editHighlight, scrollToForm } = useEditFormScroll();
  const resolvePropertyId = () => String(selectedPropertyId || localStorage.getItem('selectedPropertyId') || '').trim();
  const authHeaders = (extra = {}) => ({ Authorization: `Bearer ${token}`, 'x-property-id': resolvePropertyId(), ...extra });
  const parseResponse = async (res) => {
    const raw = await res.text();
    try {
      const json = raw ? JSON.parse(raw) : {};
      return { data: json, message: json?.error || '' };
    } catch {
      return { data: {}, message: raw || '' };
    }
  };

  useEffect(() => {
    if (!resolvePropertyId()) {
      setItems([]);
      setCategories([]);
      setError('Select a property first from Hotel Config');
      return;
    }
    setError('');
    loadItems();
    loadCategories();
  }, [token, selectedPropertyId]);

  const loadCategories = () => {
    if (!resolvePropertyId()) {
      setCategories([]);
      return;
    }
    fetch('/api/liquor-category', {
      headers: authHeaders()
    })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setCategories(data);
        } else if (data && Array.isArray(data.categories)) {
          setCategories(data.categories);
        } else {
          setCategories([]);
        }
      })
      .catch(() => setCategories([]));
  };

  const loadItems = () => {
    if (!resolvePropertyId()) {
      setItems([]);
      return;
    }
    fetch('/api/item-master', {
      headers: authHeaders()
    })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setItems(data);
        } else if (data && Array.isArray(data.items)) {
          setItems(data.items);
        } else {
          setItems([]);
        }
      })
      .catch(() => {
        setError('Failed to load item master');
        setItems([]);
      });
  };

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleCreate = async () => {
    try {
      if (!resolvePropertyId()) {
        setError('Select a property first from Hotel Config');
        return;
      }
      const res = await fetch('/api/item-master', {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(form)
      });
      const { data, message } = await parseResponse(res);
      if (res.ok) {
        const created = data;
        setItems([...items, created]);
        setForm(emptyForm);
        setError('');
      } else {
        setError(message || 'Create failed');
      }
    } catch {
      setError('Network error');
    }
  };

  const handleEdit = item => {
    setEditingMainCode(item.main_item_code);
    setForm({ ...emptyForm, ...item });
    scrollToForm();
  };

  const handleUpdate = async () => {
    try {
      if (!resolvePropertyId()) {
        setError('Select a property first from Hotel Config');
        return;
      }
      const res = await fetch(`/api/item-master/${editingMainCode}`, {
        method: 'PUT',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(form)
      });
      const { data, message } = await parseResponse(res);
      if (res.ok) {
        const updated = data;
        setItems(items.map(i => i.main_item_code === editingMainCode ? updated : i));
        setForm(emptyForm);
        setEditingMainCode(null);
        setError('');
      } else {
        setError(message || 'Update failed');
      }
    } catch {
      setError('Network error');
    }
  };

  const handleCancel = () => {
    setEditingMainCode(null);
    setForm(emptyForm);
  };

  const handleDelete = async main_item_code => {
    try {
      if (!resolvePropertyId()) {
        setError('Select a property first from Hotel Config');
        return;
      }
      const res = await fetch(`/api/item-master/${main_item_code}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      const { message } = await parseResponse(res);
      if (res.ok) {
        setItems(items.filter(i => i.main_item_code !== main_item_code));
        setError('');
      } else {
        setError(message || 'Delete failed');
      }
    } catch {
      setError('Network error');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file extension
      if (!file.name.toLowerCase().endsWith('.csv')) {
        setError('Invalid file type. Only CSV files are allowed.');
        setSelectedFile(null);
        return;
      }

      // Validate MIME type
      const allowedMimeTypes = ['text/csv', 'application/csv', 'text/plain', 'application/vnd.ms-excel'];
      if (!allowedMimeTypes.includes(file.type)) {
        setError('Invalid file type. Only CSV files with proper MIME type are allowed.');
        setSelectedFile(null);
        return;
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size exceeds 5MB limit.');
        setSelectedFile(null);
        return;
      }

      setSelectedFile(file);
      setError('');
    }
  };

  const handleDownloadSample = () => {
    window.open('/api/item-master/download/sample-csv', '_blank');
  };

  const handleBulkUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }
    if (!resolvePropertyId()) {
      setError('Select a property first from Hotel Config');
      return;
    }

    setUploading(true);
    setError('');
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('autoGenerateCodes', autoGenerateCodes);

      const res = await fetch('/api/item-master/bulk-upload', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'x-property-id': resolvePropertyId() },
        body: formData
      });

      const data = await res.json();

      if (res.ok) {
        setUploadResult(data);
        loadItems();
        if (data.successCount > 0) {
          setError('');
        }
      } else {
        setError(data.error || 'Upload failed');
        // Show MIME type or validation errors prominently
        if (data.error && data.error.includes('Invalid file type')) {
          setUploadResult({
            successCount: 0,
            failedCount: 0,
            results: { success: [], failed: [] },
            validationError: data.error
          });
        }
      }
    } catch (err) {
      setError('Network error during upload: ' + (err.message || 'Unknown error'));
    } finally {
      setUploading(false);
    }
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
    setSelectedFile(null);
    setUploadResult(null);
    setAutoGenerateCodes(false);
  };

  return (
    <div className="page-shell">
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1rem'}}>
        <h2 style={{color:'var(--theme-primary-dark)',margin:0}}>Item Master</h2>
        <button onClick={() => setShowUploadModal(true)} className="btn-success" style={{padding:'0.75rem 1.5rem',border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'1rem',display:'flex',alignItems:'center',gap:'0.5rem'}}>
          📤 Bulk Upload
        </button>
      </div>
      {error && <div className="error-banner">{error}</div>}
      <div className="filter-bar" ref={formRef} style={{marginBottom:'2rem', ...editHighlightStyle(editHighlight)}}>
        <h3>{editingMainCode ? 'Edit Item' : 'Add New Item'}</h3>
        <form style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))',gap:'1rem'}}>
          <input name="main_item_code" placeholder="Main Item Code" value={form.main_item_code} onChange={handleChange} disabled={editingMainCode !== null} style={{padding:'0.75rem',borderRadius:'6px',border:'1px solid #dcdde1'}} />
          <input name="main_item_name" placeholder="Main Item Name" value={form.main_item_name} onChange={handleChange} style={{padding:'0.75rem',borderRadius:'6px',border:'1px solid #dcdde1'}} />
          <select name="liquor_category_id" value={form.liquor_category_id} onChange={handleChange} style={{padding:'0.75rem',borderRadius:'6px',border:'1px solid #dcdde1'}}>
            <option value="">-- Select Liquor Category --</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
          <input name="brand" placeholder="Brand" value={form.brand} onChange={handleChange} style={{padding:'0.75rem',borderRadius:'6px',border:'1px solid #dcdde1'}} />
          <input name="hsn_code" placeholder="HSN Code" value={form.hsn_code} onChange={handleChange} style={{padding:'0.75rem',borderRadius:'6px',border:'1px solid #dcdde1'}} />
          <input name="quantity_ml_per_unit" placeholder="Quantity in ML per Unit" value={form.quantity_ml_per_unit} onChange={handleChange} style={{padding:'0.75rem',borderRadius:'6px',border:'1px solid #dcdde1'}} />
        </form>
        <div style={{marginTop:'1rem',display:'flex',gap:'1rem'}}>
          {editingMainCode ? (
            <>
              <button type="button" onClick={handleUpdate} className="btn-success" style={{padding:'0.75rem 2rem',border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'1rem'}}>Update Item</button>
              <button type="button" onClick={handleCancel} className="btn-muted" style={{padding:'0.75rem 2rem',border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'1rem'}}>Cancel</button>
            </>
          ) : (
            <button type="button" onClick={handleCreate} className="btn-primary" style={{padding:'0.75rem 2rem',border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'1rem'}}>Add Item</button>
          )}
        </div>
      </div>
      <h3>Item List</h3>
      {!items || items.length === 0 ? (
        <p>No items in master</p>
      ) : (
        <table style={{width:'100%',borderCollapse:'collapse',background:'#fff'}}>
          <thead>
            <tr style={{background:'var(--theme-primary-dark)',color:'#fff'}}>
              <th style={{padding:'0.75rem',textAlign:'left',borderBottom:'2px solid #dcdde1'}}>Item Code</th>
              <th style={{padding:'0.75rem',textAlign:'left',borderBottom:'2px solid #dcdde1'}}>Item Name</th>
              <th style={{padding:'0.75rem',textAlign:'left',borderBottom:'2px solid #dcdde1'}}>Category</th>
              <th style={{padding:'0.75rem',textAlign:'left',borderBottom:'2px solid #dcdde1'}}>Brand</th>
              <th style={{padding:'0.75rem',textAlign:'left',borderBottom:'2px solid #dcdde1'}}>HSN Code</th>
              <th style={{padding:'0.75rem',textAlign:'left',borderBottom:'2px solid #dcdde1'}}>Qty ML/Unit</th>
              <th style={{padding:'0.75rem',textAlign:'left',borderBottom:'2px solid #dcdde1'}}>Action</th>
            </tr>
          </thead>
          <tbody>
            {items.map(item => (
              <tr key={item.main_item_code} style={{borderBottom:'1px solid #dcdde1'}}>
                <td style={{padding:'0.75rem'}}>{item.main_item_code}</td>
                <td style={{padding:'0.75rem'}}>{item.main_item_name}</td>
                <td style={{padding:'0.75rem'}}>{categories.find(c => c.id == item.liquor_category_id)?.name || '-'}</td>
                <td style={{padding:'0.75rem'}}>{item.brand}</td>
                <td style={{padding:'0.75rem'}}>{item.hsn_code}</td>
                <td style={{padding:'0.75rem'}}>{item.quantity_ml_per_unit || 0}</td>
                <td style={{padding:'0.75rem',display:'flex',gap:'0.5rem'}}>
                  <button onClick={() => handleEdit(item)} style={{background:'#3498db',color:'#fff',border:'none',padding:'0.5rem 1rem',borderRadius:'4px',cursor:'pointer'}}>Edit</button>
                  <button onClick={() => handleDelete(item.main_item_code)} className="btn-danger" style={{border:'none',padding:'0.5rem 1rem',borderRadius:'4px',cursor:'pointer'}}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Bulk Upload Modal */}
      {showUploadModal && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div style={{background:'#fff',borderRadius:'12px',padding:isMobile ? '1rem' : '2rem',maxWidth:'600px',width:isMobile ? '95%' : '90%',maxHeight:'80vh',overflowY:'auto',boxShadow:'0 4px 20px rgba(0,0,0,0.3)'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1.5rem'}}>
              <h3 style={{margin:0,color:'var(--theme-primary-dark)'}}>📤 Bulk Upload Items</h3>
              <button onClick={closeUploadModal} className="btn-danger" style={{border:'none',padding:'0.5rem 1rem',borderRadius:'6px',cursor:'pointer',fontSize:'1rem'}}>✖</button>
            </div>

            <div style={{marginBottom:'1.5rem',padding:'1rem',background:'#e8f5e9',borderRadius:'8px',border:'1px solid #4caf50'}}>
              <h4 style={{margin:'0 0 0.5rem 0',color:'#2e7d32'}}>📋 Instructions</h4>
              <ul style={{margin:0,paddingLeft:'1.5rem',fontSize:'0.9rem',color:'#1b5e20'}}>
                <li>Download the sample CSV file to see the required format</li>
                <li>Fill in your items data following the same structure</li>
                <li>If item code is empty, enable "Auto-generate codes" option</li>
                <li>Save as CSV file and upload below</li>
              </ul>
            </div>

            <button onClick={handleDownloadSample} style={{padding:'0.75rem 1.5rem',background:'#3498db',color:'#fff',border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'1rem',marginBottom:'1.5rem',width:'100%'}}>
              📥 Download Sample CSV
            </button>

            <div style={{marginBottom:'1.5rem'}}>
              <label style={{display:'block',marginBottom:'0.5rem',fontWeight:600,color:'#2c3e50'}}>Select CSV File:</label>
              <input type="file" accept=".csv" onChange={handleFileChange} style={{padding:'0.75rem',width:'100%',border:'2px dashed #dcdde1',borderRadius:'6px',cursor:'pointer'}} />
              {selectedFile && <div style={{marginTop:'0.5rem',color:'#27ae60',fontSize:'0.9rem'}}>✓ Selected: {selectedFile.name}</div>}
            </div>

            <div style={{marginBottom:'1.5rem',padding:'1rem',background:'#fff3cd',borderRadius:'8px',border:'1px solid #ffc107'}}>
              <label style={{display:'flex',alignItems:'center',gap:'0.5rem',cursor:'pointer',fontWeight:600,color:'#856404'}}>
                <input type="checkbox" checked={autoGenerateCodes} onChange={(e) => setAutoGenerateCodes(e.target.checked)} />
                <span>🔢 Auto-generate codes for empty item codes</span>
              </label>
              <p style={{margin:'0.5rem 0 0 1.7rem',fontSize:'0.85rem',color:'#856404'}}>
                System will generate codes like ITEM0001, ITEM0002, etc. for rows with empty item codes
              </p>
            </div>

            {uploadResult && (
              <div style={{marginBottom:'1.5rem',padding:'1rem',background:uploadResult.successCount > 0 ? '#d4edda' : '#f8d7da',borderRadius:'8px',border:`1px solid ${uploadResult.successCount > 0 ? '#28a745' : '#dc3545'}`}}>
                <h4 style={{margin:'0 0 0.5rem 0',color:uploadResult.successCount > 0 ? '#155724' : '#721c24'}}>Upload Results</h4>
                <div style={{fontSize:'0.9rem'}}>
                  <div>✅ Success: {uploadResult.successCount} items</div>
                  <div>❌ Failed: {uploadResult.failedCount} items</div>
                  {uploadResult.emptyCodeCount > 0 && <div>🔢 Empty codes found: {uploadResult.emptyCodeCount}</div>}
                </div>
                {uploadResult.results.failed.length > 0 && (
                  <details style={{marginTop:'0.75rem'}}>
                    <summary style={{cursor:'pointer',fontWeight:600,color:'#721c24'}}>View Failed Items ({uploadResult.results.failed.length})</summary>
                    <div style={{marginTop:'0.5rem',maxHeight:'150px',overflowY:'auto',fontSize:'0.85rem'}}>
                      {uploadResult.results.failed.map((f, i) => (
                        <div key={i} style={{padding:'0.5rem',background:'#fff',borderRadius:'4px',marginBottom:'0.3rem',border:'1px solid #f5c6cb'}}>
                          <strong>{f.row.main_item_name || 'Unknown'}</strong>: {f.reason}
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            )}

            <div style={{display:'flex',gap:'1rem'}}>
              <button onClick={handleBulkUpload} disabled={!selectedFile || uploading} style={{flex:1,padding:'0.75rem',background:uploading ? '#95a5a6' : '#27ae60',color:'#fff',border:'none',borderRadius:'6px',cursor:uploading || !selectedFile ? 'not-allowed' : 'pointer',fontSize:'1rem',opacity:uploading || !selectedFile ? 0.6 : 1}}>
                {uploading ? '⏳ Uploading...' : '📤 Upload Items'}
              </button>
              <button onClick={closeUploadModal} className="btn-muted" style={{padding:'0.75rem 1.5rem',border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'1rem'}}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ItemMaster;
